// Ring buffers for context that only makes sense at crash time. Kept in
// module closure (NOT Vuex state) — they are diagnostic infrastructure,
// mutate constantly, and putting them in a reactive store would defeat
// the point (every push would trigger reactivity).
const BREADCRUMB_LIMIT = 20
const WARNING_LIMIT = 5
const WARNING_MAX_LEN = 300
const PARENT_CHAIN_MAX_HOPS = 10
const SESSION_ID_KEY = 'fe_error_session_id'

let breadcrumbs = []
let vue_warnings = []

function _Push_Breadcrumb (type, name) {
  if (!name) return
  breadcrumbs.push(type + ':' + name)
  if (breadcrumbs.length > BREADCRUMB_LIMIT) breadcrumbs.shift()
}

function _Push_Warning (msg) {
  vue_warnings.push(String(msg).slice(0, WARNING_MAX_LEN))
  if (vue_warnings.length > WARNING_LIMIT) vue_warnings.shift()
}

function _Parent_Chain (vm) {
  const chain = []
  let cur = vm
  let hops = 0
  while (cur && hops < PARENT_CHAIN_MAX_HOPS) {
    const opts = (cur && cur.$options) || {}
    const name = opts.name || opts._componentTag || null
    if (name) chain.push(name)
    cur = cur.$parent
    hops++
  }
  return chain
}

function _Session_Id () {
  try {
    if (typeof sessionStorage === 'undefined') return null
    let id = sessionStorage.getItem(SESSION_ID_KEY)
    if (!id) {
      id = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : 's-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10)
      sessionStorage.setItem(SESSION_ID_KEY, id)
    }
    return id
  } catch (_) {
    return null
  }
}

function _User_Agent () {
  return (typeof navigator !== 'undefined' && navigator.userAgent) || null
}

export function Install_Error_Reporter (Vue, router, store) {
  function _Route () {
    return (router && router.currentRoute && router.currentRoute.fullPath) || null
  }

  function _Context (vm) {
    return {
      parent_chain: _Parent_Chain(vm),
      breadcrumbs: breadcrumbs.slice(),
      vue_warnings: vue_warnings.slice(),
      user_agent: _User_Agent(),
      session_id: _Session_Id(),
      timestamp_iso: new Date().toISOString()
    }
  }

  // Vue's own warnings — most importantly "You may have an infinite update
  // loop in a component render function". If that string appears in
  // vue_warnings right before a RangeError report, the crash is a watcher
  // loop, NOT observe/defineReactive recursion. Distinguishing the two
  // is the difference between a 5-minute fix and a full audit.
  Vue.config.warnHandler = function (msg /*, vm, trace */) {
    _Push_Warning(msg)
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[Vue warn]', msg)
    }
  }

  Vue.config.errorHandler = function (err, vm, info) {
    const opts = (vm && vm.$options) || {}
    store.dispatch('report_fe_error', Object.assign({
      err: err,
      route: _Route(),
      component_name: opts.name || opts._componentTag || null,
      info: info
    }, _Context(vm)))
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(err)
    }
  }

  window.addEventListener('error', function (e) {
    store.dispatch('report_fe_error', Object.assign({
      err: e.error || new Error(e.message),
      route: _Route()
    }, _Context(null)))
  })

  window.addEventListener('unhandledrejection', function (e) {
    const reason = e.reason
    if (reason && reason.isAxiosError) return
    const err = reason instanceof Error ? reason : new Error(String(reason))
    store.dispatch('report_fe_error', Object.assign({
      err: err,
      route: _Route()
    }, _Context(null)))
  })

  // Breadcrumb sources. Names only — payloads/params can carry PII (CNP,
  // telefon, email, CI) so we never log them.
  if (router && typeof router.afterEach === 'function') {
    router.afterEach(function (to) {
      _Push_Breadcrumb('nav', to && (to.name || to.path))
    })
  }
  if (store && typeof store.subscribeAction === 'function') {
    store.subscribeAction(function (action) {
      _Push_Breadcrumb('act', action && action.type)
    })
  }
  if (store && typeof store.subscribe === 'function') {
    store.subscribe(function (mutation) {
      _Push_Breadcrumb('mut', mutation && mutation.type)
    })
  }
}

// Test-only: reset ring buffers between tests. Not called from app code.
export function _Reset_Reporter_Context_For_Tests () {
  breadcrumbs = []
  vue_warnings = []
}
